import {
  Chip,
  CircularProgress,
  createTheme,
  Dialog as MuiDialog,
  DialogContent as MuiDialogContent,
  DialogTitle as MuiDialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ThemeProvider,
  Tabs,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  PageHeader,
  SpinnerLoader,
  Toastify,
} from "lift-ui";
import { AlertTriangle, Clapperboard, Lock, RefreshCw } from "lucide-react";
import { useAuthContext } from "@lift-admin/utils/provider";
import { useProcessContext } from "../process-context";
import { statusColorMap } from "../integration-test/color-map";
import {
  formatRunSummary,
  getPlaywrightRunArtifacts,
  getPlaywrightRun,
  getPlaywrightRunLogs,
  getPlaywrightRuns,
  getPlaywrightTestCatalog,
  PlaywrightArtifactManifest,
  PlaywrightCatalogSpec,
  PlaywrightLogEntry,
  PlaywrightRun,
  PlaywrightRunReport,
} from "./playwright-api";
import ArtifactDownloads from "./artifact-downloads";
import ArtifactGallery from "./artifact-gallery";
import LabelSelect from "./label-select";
import RunTestFilter from "./run-test-filter";
import RunTestList from "./run-test-list";
import {
  entryMatchesSelectedTests,
  isIdentityLogLabel,
  selectedTests,
  testKeyForLogEntry,
} from "./run-test-selection";
import TestSelect from "./test-select";

const theme = createTheme({
  typography: { fontFamily: '"Figtree", sans-serif' },
  palette: { primary: { main: "#16306D" } },
});

const terminalStatuses = new Set(["succeeded", "failed"]);
const displayStatus = (status?: string) => (status === "succeeded" ? "completed" : status || "queued");
const statusStyle = (status?: string) => {
  const colorStatus = status === "passed" ? "completed" : displayStatus(status);
  return statusColorMap[colorStatus] ?? { background: "#E2E3E5", color: "#383D41" };
};
const splitLabels = (value: string) =>
  Array.from(new Set(value.split(",").map((label) => label.trim()).filter(Boolean)));
const formatTime = (value?: string) => (value ? new Date(value).toLocaleString() : "—");

const StatusChip = ({ status }: { status?: string }) => {
  const colors = statusStyle(status);
  return (
    <Chip
      size="small"
      label={displayStatus(status)}
      sx={{
        backgroundColor: colors.background,
        border: `1px solid ${colors.color}`,
        color: colors.color,
        fontWeight: 600,
        textTransform: "capitalize",
      }}
    />
  );
};

const PlayWrightTrigger = () => {
  const { canManage } = useAuthContext();
  const hasManageAccess = canManage("taxonomy");
  const {
    handlePlaywrightTrigger,
    handleCRMSync,
    isTriggering,
    isCRMSyncing,
    crmSyncDialogOpen,
    setCRMSyncDialogOpen,
  } = useProcessContext();

  const [runs, setRuns] = useState<PlaywrightRun[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [environment, setEnvironment] = useState("dev");
  const [catalogSpecs, setCatalogSpecs] = useState<PlaywrightCatalogSpec[]>([]);
  const [suggestedLabels, setSuggestedLabels] = useState<string[]>([]);
  const [catalogReady, setCatalogReady] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [selectedTestNames, setSelectedTestNames] = useState<string[]>([]);
  const [triggerLabels, setTriggerLabels] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [environmentFilter, setEnvironmentFilter] = useState("");
  const [labelFilters, setLabelFilters] = useState<string[]>([]);
  const [labelInput, setLabelInput] = useState("");
  const [query, setQuery] = useState("");
  const [selectedRun, setSelectedRun] = useState<PlaywrightRun | null>(null);
  const [report, setReport] = useState<PlaywrightRunReport | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [logQuery, setLogQuery] = useState("");
  const [levelFilter, setLevelFilter] = useState("");
  const [entryStatusFilter, setEntryStatusFilter] = useState("");
  const [entryLabelFilters, setEntryLabelFilters] = useState<string[]>([]);
  const [detailTab, setDetailTab] = useState<"logs" | "artifacts">("logs");
  const [selectedTestKeys, setSelectedTestKeys] = useState<string[]>([]);
  const [artifactManifest, setArtifactManifest] =
    useState<PlaywrightArtifactManifest | null>(null);
  const [artifactsLoading, setArtifactsLoading] = useState(false);
  const fetchedReports = useRef(new Map<string, PlaywrightRunReport>());

  useEffect(() => {
    let active = true;
    setCatalogLoading(true);
    getPlaywrightTestCatalog()
      .then((catalog) => {
        if (!active) return;
        setCatalogSpecs(catalog.specs);
        setSuggestedLabels(catalog.suggestedLabels);
        setCatalogReady(catalog.ready);
        setSelectedTestNames(catalog.ready ? catalog.specs.map((spec) => spec.id) : []);
      })
      .catch(() => {
        if (!active) return;
        setCatalogSpecs([]);
        setSuggestedLabels([]);
        setCatalogReady(false);
      })
      .finally(() => {
        if (active) setCatalogLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const loadRuns = useCallback(async () => {
    setRunsLoading(true);
    try {
      const nextRuns = await getPlaywrightRuns({
        status: statusFilter || undefined,
        label: labelFilters,
        q: query.trim() || undefined,
      });
      setRuns(nextRuns.filter((run) => Boolean(run.id)));
    } catch (error: any) {
      Toastify("error", error?.response?.data?.message || "Unable to load Playwright runs");
    } finally {
      setRunsLoading(false);
    }
  }, [labelFilters, query, statusFilter]);

  useEffect(() => {
    const timeout = window.setTimeout(loadRuns, 250);
    return () => window.clearTimeout(timeout);
  }, [loadRuns]);

  const loadReportOnce = useCallback(async (run: PlaywrightRun) => {
    if (!run.id) return;
    const cachedReport = fetchedReports.current.get(run.id);
    if (cachedReport) {
      setReport(cachedReport);
      return;
    }
    try {
      const nextReport = await getPlaywrightRunLogs(run.id);
      fetchedReports.current.set(run.id, nextReport);
      setReport(nextReport);
    } catch (error: any) {
      Toastify("error", error?.response?.data?.message || "Unable to load Playwright report");
    }
  }, []);

  useEffect(() => {
    if (!selectedRun?.id) return;
    let active = true;
    let intervalId: number | undefined;

    const refreshDetail = async () => {
      try {
        const run = await getPlaywrightRun(selectedRun.id);
        if (!active) return;
        setSelectedRun(run);
        setRuns((current) => current.map((item) => (item.id === run.id ? { ...item, ...run } : item)));
        if (run.gcsReportReady || terminalStatuses.has(run.status)) {
          await loadReportOnce(run);
          if (intervalId) window.clearInterval(intervalId);
        }
      } catch (error: any) {
        if (active) {
          Toastify("error", error?.response?.data?.message || "Unable to refresh Playwright run");
          if (intervalId) window.clearInterval(intervalId);
        }
      } finally {
        if (active) setDetailLoading(false);
      }
    };

    setDetailLoading(true);
    refreshDetail();
    if (!terminalStatuses.has(selectedRun.status) && !selectedRun.gcsReportReady) {
      intervalId = window.setInterval(refreshDetail, 2000);
    }
    return () => {
      active = false;
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [loadReportOnce, selectedRun?.id]);

  useEffect(() => {
    if (!selectedRun?.id) return;
    let active = true;
    setArtifactsLoading(true);
    setArtifactManifest(null);
    getPlaywrightRunArtifacts(selectedRun.id)
      .then((manifest) => {
        if (active) setArtifactManifest(manifest);
      })
      .catch((error: any) => {
        if (!active) return;
        setArtifactManifest({
          artifactsReady: false,
          artifactCount: 0,
          artifacts: [],
          artifactError:
            error?.response?.data?.message || "Unable to load run artifacts",
        });
      })
      .finally(() => {
        if (active) setArtifactsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [selectedRun?.id]);

  const filteredRuns = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return runs.filter((run) => {
      if (!run.id) return false;
      const hasLabels = labelFilters.every((label) => run.labels?.includes(label));
      const hasEnvironment = !environmentFilter || run.environment === environmentFilter;
      const hasStatus = !statusFilter || run.status === statusFilter;
      const searchable = [
        run.source,
        run.environment,
        run.testName,
        formatRunSummary(run.summary),
        ...(run.labels ?? []),
      ].join(" ").toLowerCase();
      return hasLabels && hasEnvironment && hasStatus && (!normalizedQuery || searchable.includes(normalizedQuery));
    });
  }, [environmentFilter, labelFilters, query, runs, statusFilter]);

  const facetOptions = useMemo(() => {
    const entries = report?.entries ?? [];
    return {
      levels: Array.from(new Set(entries.map((entry) => entry.level).filter(Boolean))).sort(),
      statuses: Array.from(new Set(entries.map((entry) => entry.status).filter(Boolean))).sort(),
    };
  }, [report]);

  const activeTests = useMemo(
    () => selectedTests(report?.tests ?? [], selectedTestKeys),
    [report, selectedTestKeys]
  );

  const filteredEntries = useMemo(() => {
    const normalizedQuery = logQuery.trim().toLowerCase();
    return (report?.entries ?? []).filter((entry) => {
      const searchable = [
        entry.message,
        entry.level,
        entry.status,
        entry.spec,
        entry.test,
        ...(entry.labels ?? []),
      ].join(" ").toLowerCase();
      return (
        (!normalizedQuery || searchable.includes(normalizedQuery)) &&
        (!levelFilter || entry.level === levelFilter) &&
        (!entryStatusFilter || entry.status === entryStatusFilter) &&
        entryMatchesSelectedTests(entry, activeTests) &&
        entryLabelFilters.every((label) => entry.labels?.includes(label))
      );
    });
  }, [
    activeTests,
    entryLabelFilters,
    entryStatusFilter,
    levelFilter,
    logQuery,
    report,
  ]);

  const runTrigger = async () => {
    if (!hasManageAccess) {
      Toastify("error", "Only managers can trigger. Contact your administrator for access.");
      return;
    }
    const queued = await handlePlaywrightTrigger({
      environment,
      ...(catalogReady && selectedTestNames.length < catalogSpecs.length
        ? { testNames: selectedTestNames }
        : {}),
      ...(triggerLabels.length ? { labels: triggerLabels } : {}),
    });
    if (queued) await loadRuns();
  };

  const openRun = (run: PlaywrightRun) => {
    if (!run.id) return;
    setReport(null);
    setLogQuery("");
    setLevelFilter("");
    setEntryStatusFilter("");
    setEntryLabelFilters([]);
    setDetailTab("logs");
    setSelectedTestKeys([]);
    setArtifactManifest(null);
    setSelectedRun(run);
  };

  const addCatalogLabel = () => {
    const labels = splitLabels(labelInput);
    if (labels.length) {
      setLabelFilters((current) => Array.from(new Set([...current, ...labels])));
      setLabelInput("");
    }
  };

  const addEntryLabel = (label: string) =>
    setEntryLabelFilters((current) => current.includes(label) ? current : [...current, label]);

  const handleLogChip = (entry: PlaywrightLogEntry, label: string) => {
    if (isIdentityLogLabel(label)) {
      const key = testKeyForLogEntry(entry, report?.tests ?? []);
      if (key) setSelectedTestKeys([key]);
      return;
    }
    addEntryLabel(label);
  };

  const logColor = (entry: PlaywrightLogEntry) => {
    if (entry.level === "error" || entry.status === "failed") return "#B42318";
    if (entry.level === "warn" || entry.level === "warning") return "#B54708";
    if (entry.status === "passed" || entry.level === "info") return "#027A48";
    return "#344054";
  };

  return (
    <ThemeProvider theme={theme}>
      <div className="flex flex-col h-full bg-white">
        <PageHeader
          title="Triggers"
          renderCustomTop={
            <div className="flex items-center gap-3">
              {!hasManageAccess ? (
                <div className="flex items-center gap-2 px-3 py-2 border">
                  <Lock className="h-4 w-4" />
                  <span className="text-sm">Only managers can trigger.</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 px-3 py-2 border border-amber-400 rounded-md bg-amber-50 text-amber-800">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="text-sm">
                    Keep this panel open while CRM Sync is in progress.
                  </span>
                </div>
              )}
            </div>
          }
        />

        <div className="flex-1 px-8 pt-4 pb-8 overflow-auto space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Paper variant="outlined" sx={{ p: 3 }}>
              <div className="flex items-center gap-3 mb-4">
                <Clapperboard className="h-10 w-10 text-[#334238]" />
                <div>
                  <Typography variant="h6">Playwright tests</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Queue a run and monitor it below.
                  </Typography>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3 items-start">
                <FormControl size="small">
                  <InputLabel>Environment</InputLabel>
                  <Select
                    label="Environment"
                    value={environment}
                    onChange={(event) => setEnvironment(event.target.value)}
                  >
                    {["dev", "stg", "prod"].map((value) => (
                      <MenuItem key={value} value={value}>{value}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TestSelect
                  specs={catalogSpecs}
                  ready={catalogReady}
                  loading={catalogLoading}
                  value={selectedTestNames}
                  onChange={setSelectedTestNames}
                />
                <LabelSelect
                  options={suggestedLabels}
                  value={triggerLabels}
                  onChange={setTriggerLabels}
                  loading={catalogLoading}
                />
              </div>
              <Button
                onClick={runTrigger}
                disabled={
                  !hasManageAccess ||
                  isTriggering ||
                  (catalogReady && selectedTestNames.length === 0)
                }
              >
                {isTriggering ? <SpinnerLoader className="h-4 w-4 mr-2" /> : null}
                {isTriggering ? "Queuing..." : "Queue Playwright run"}
              </Button>
            </Paper>

            <Paper variant="outlined" sx={{ p: 3 }}>
              <div className="flex items-center gap-3 mb-4">
                <RefreshCw className="h-10 w-10 text-[#334238]" />
                <div>
                  <Typography variant="h6">CRM Sync</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Existing CRM synchronization workflow.
                  </Typography>
                </div>
              </div>
              <Button
                onClick={() => hasManageAccess && handleCRMSync()}
                disabled={!hasManageAccess || isCRMSyncing}
              >
                {isCRMSyncing ? <SpinnerLoader className="h-4 w-4 mr-2" /> : null}
                {isCRMSyncing ? "Processing..." : "Start CRM Sync"}
              </Button>
            </Paper>
          </div>

          <Paper variant="outlined" sx={{ p: 3 }}>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <Typography variant="h6">Playwright run history</Typography>
                <Typography variant="body2" color="text.secondary">
                  Select a run to inspect tests and logs.
                </Typography>
              </div>
              <Button variant="outline" onClick={loadRuns} disabled={runsLoading}>
                {runsLoading ? <SpinnerLoader className="h-4 w-4 mr-2" /> : null}
                Refresh
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
              <FormControl size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                >
                  <MenuItem value="">All statuses</MenuItem>
                  {["queued", "running", "succeeded", "failed"].map((value) => (
                    <MenuItem key={value} value={value}>{displayStatus(value)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small">
                <InputLabel>Environment</InputLabel>
                <Select
                  label="Environment"
                  value={environmentFilter}
                  onChange={(event) => setEnvironmentFilter(event.target.value)}
                >
                  <MenuItem value="">All environments</MenuItem>
                  {Array.from(new Set(runs.map((run) => run.environment))).sort().map((value) => (
                    <MenuItem key={value} value={value}>{value}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Labels (AND)"
                value={labelInput}
                onChange={(event) => setLabelInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    addCatalogLabel();
                  }
                }}
                onBlur={addCatalogLabel}
              />
              <TextField
                size="small"
                label="Search runs"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
            </div>
            {labelFilters.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {labelFilters.map((label) => (
                  <Chip
                    key={label}
                    size="small"
                    label={label}
                    onDelete={() => setLabelFilters((current) => current.filter((item) => item !== label))}
                  />
                ))}
              </div>
            )}

            <TableContainer sx={{ maxHeight: 480 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {["Time", "Source", "Environment", "Test name", "Summary", "Status"].map((heading) => (
                      <TableCell key={heading} sx={{ color: "text.secondary", fontWeight: 600 }}>
                        {heading}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRuns.map((run) => (
                    <TableRow
                      hover
                      key={run.id}
                      onClick={() => openRun(run)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>{formatTime(run.startedAt || run.finishedAt)}</TableCell>
                      <TableCell>{run.source || "—"}</TableCell>
                      <TableCell>{run.environment || "—"}</TableCell>
                      <TableCell>{run.testName || "All tests"}</TableCell>
                      <TableCell sx={{ maxWidth: 360 }}>{formatRunSummary(run.summary) || "—"}</TableCell>
                      <TableCell><StatusChip status={run.status} /></TableCell>
                    </TableRow>
                  ))}
                  {!runsLoading && filteredRuns.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 5, color: "text.secondary" }}>
                        No Playwright runs match these filters.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {runsLoading && (
              <div className="flex justify-center py-6"><CircularProgress size={28} /></div>
            )}
          </Paper>
        </div>

        <MuiDialog
          open={Boolean(selectedRun)}
          onClose={() => setSelectedRun(null)}
          fullWidth
          maxWidth="xl"
        >
          <MuiDialogTitle>
            <div className="flex flex-wrap items-center gap-3">
              <span>{selectedRun?.testName || "Playwright run"}</span>
              <StatusChip status={selectedRun?.status} />
              {detailLoading && <CircularProgress size={18} />}
            </div>
          </MuiDialogTitle>
          <MuiDialogContent dividers sx={{ backgroundColor: "#F9FAFB" }}>
            {selectedRun && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
                <div><Typography variant="caption">Environment</Typography><Typography>{selectedRun.environment}</Typography></div>
                <div><Typography variant="caption">Source</Typography><Typography>{selectedRun.source}</Typography></div>
                <div><Typography variant="caption">Started</Typography><Typography>{formatTime(selectedRun.startedAt)}</Typography></div>
                <div><Typography variant="caption">Finished</Typography><Typography>{formatTime(selectedRun.finishedAt)}</Typography></div>
                <div><Typography variant="caption">Event ID</Typography><Typography sx={{ wordBreak: "break-all" }}>{selectedRun.eventId}</Typography></div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
              <Paper variant="outlined" sx={{ p: 2, maxHeight: 650, overflow: "auto" }}>
                <Typography variant="subtitle1" fontWeight={600} mb={1}>Tests</Typography>
                {!report && <Typography color="text.secondary">Report is not ready yet.</Typography>}
                {report && (
                  <RunTestList
                    tests={report.tests}
                    selectedKeys={selectedTestKeys}
                    artifactCount={artifactManifest?.artifactCount ?? 0}
                    onSelect={setSelectedTestKeys}
                    statusChip={(status) => <StatusChip status={status} />}
                  />
                )}
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, minWidth: 0 }}>
                <Tabs
                  value={detailTab}
                  onChange={(_, value) => setDetailTab(value)}
                  sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
                >
                  <Tab value="logs" label="Logs" />
                  <Tab
                    value="artifacts"
                    label={`Artifacts (${artifactManifest?.artifactCount ?? 0})`}
                  />
                </Tabs>
                {detailTab === "artifacts" && selectedRun ? (
                  <div className="space-y-4">
                    <ArtifactDownloads
                      runId={selectedRun.id}
                      testKey={
                        selectedTestKeys.length === 1 ? selectedTestKeys[0] : null
                      }
                      runBundleAvailable={artifactManifest?.bundles?.run}
                      testBundleAvailable={
                        selectedTestKeys.length === 1
                          ? artifactManifest?.bundles?.tests?.includes(
                              selectedTestKeys[0]
                            )
                          : false
                      }
                    />
                    <ArtifactGallery
                      manifest={artifactManifest}
                      loading={artifactsLoading}
                      runId={selectedRun.id}
                      selectedTestKeys={selectedTestKeys}
                    />
                  </div>
                ) : (
                  <>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2 mb-3">
                  <TextField
                    size="small"
                    label="Search logs"
                    value={logQuery}
                    onChange={(event) => setLogQuery(event.target.value)}
                  />
                  {[
                    ["Level", levelFilter, setLevelFilter, facetOptions.levels],
                    ["Status", entryStatusFilter, setEntryStatusFilter, facetOptions.statuses],
                  ].map(([label, value, setter, options]) => (
                    <FormControl size="small" key={label as string}>
                      <InputLabel>{label as string}</InputLabel>
                      <Select
                        label={label as string}
                        value={value as string}
                        onChange={(event) => (setter as React.Dispatch<React.SetStateAction<string>>)(event.target.value)}
                      >
                        <MenuItem value="">All</MenuItem>
                        {(options as string[]).map((option) => (
                          <MenuItem key={option} value={option}>{option}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ))}
                  <RunTestFilter
                    tests={report?.tests ?? []}
                    value={selectedTestKeys}
                    onChange={setSelectedTestKeys}
                  />
                </div>
                {entryLabelFilters.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {entryLabelFilters.map((label) => (
                      <Chip
                        key={label}
                        size="small"
                        label={label}
                        onDelete={() => setEntryLabelFilters((current) => current.filter((item) => item !== label))}
                      />
                    ))}
                  </div>
                )}
                <div className="max-h-[540px] overflow-auto font-mono text-xs">
                  {filteredEntries.map((entry, index) => (
                    <div
                      key={`${entry.ts}-${index}`}
                      className="border-b border-gray-200 py-2"
                      style={{ color: logColor(entry) }}
                    >
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="text-gray-500">{formatTime(entry.ts)}</span>
                        <Chip size="small" label={entry.level || "log"} />
                        {entry.status && <Chip size="small" label={entry.status} />}
                        {entry.spec && <Chip size="small" label={entry.spec} />}
                        {entry.test && <Chip size="small" label={entry.test} />}
                        {(entry.labels ?? []).map((label) => (
                          <Chip key={label} size="small" label={label} onClick={() => handleLogChip(entry, label)} />
                        ))}
                      </div>
                      <div className="whitespace-pre-wrap break-words">{entry.message}</div>
                    </div>
                  ))}
                  {report && filteredEntries.length === 0 && (
                    <Typography color="text.secondary" align="center" sx={{ py: 5 }}>
                      No log entries match these filters.
                    </Typography>
                  )}
                  {!report && (
                    <Typography color="text.secondary" align="center" sx={{ py: 5 }}>
                      Logs will load when the report is ready.
                    </Typography>
                  )}
                </div>
                  </>
                )}
              </Paper>
            </div>
          </MuiDialogContent>
        </MuiDialog>

        <Dialog open={crmSyncDialogOpen} onOpenChange={setCRMSyncDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle>Process Already Running</DialogTitle></DialogHeader>
            <div className="py-4">
              <p className="text-sm text-gray-600">
                The CRM Sync process is already running. Please wait until it completes before triggering again.
              </p>
            </div>
            <DialogFooter><Button onClick={() => setCRMSyncDialogOpen(false)}>OK</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </ThemeProvider>
  );
};

export default PlayWrightTrigger;
